import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Settings, Plus, Trash2, Edit3, Save, X, Loader2,
  CheckCircle2, AlertCircle, Sparkles
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = ["residential", "commercial", "town_house", "ground_up", "tenant_improvement"];
const CATEGORIES = ["renovation", "ground_up", "tenant_improvement", "other"];
const REGIONS = ["Miami", "Orlando", "Tampa", "Jacksonville", "Fort Lauderdale", "Other"];
const TRADES = ["demolition", "structural", "electrical", "plumbing", "hvac", "waterproofing", "finishes", "flooring", "fixtures", "painting", "carpentry", "masonry", "roofing", "sitework", "permits", "general"];

const emptyRule = {
  name: "",
  description: "",
  conditions: { project_types: [], categories: [], regions: [], min_value: null, max_value: null },
  actions: { fee_percent: null, margin_percent: null, contingency_percent: null, loss_factors: {} },
  active: true,
  priority: 100,
};

export default function Workspace() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // id or 'new'
  const [draft, setDraft] = useState(emptyRule);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/company/rules`); setRules(r.data); }
    catch { toast.error("Failed to load rules"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing("new"); setDraft(emptyRule); };
  const startEdit = (rule) => { setEditing(rule.id); setDraft(JSON.parse(JSON.stringify(rule))); };
  const cancel = () => { setEditing(null); setDraft(emptyRule); };

  const save = async () => {
    if (!draft.name.trim()) return toast.error("Name required");
    try {
      if (editing === "new") {
        await axios.post(`${API}/company/rules`, draft);
        toast.success("Rule created");
      } else {
        await axios.put(`${API}/company/rules/${editing}`, draft);
        toast.success("Rule updated");
      }
      setEditing(null);
      setDraft(emptyRule);
      load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this rule?")) return;
    try { await axios.delete(`${API}/company/rules/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const toggleArrayItem = (path, value) => {
    const arr = draft.conditions[path] || [];
    const next = arr.includes(value) ? arr.filter(x => x !== value) : [...arr, value];
    setDraft({ ...draft, conditions: { ...draft.conditions, [path]: next } });
  };

  const setLossFactor = (trade, value) => {
    const next = { ...(draft.actions.loss_factors || {}) };
    if (value === "" || value == null) delete next[trade];
    else next[trade] = parseFloat(value) || 0;
    setDraft({ ...draft, actions: { ...draft.actions, loss_factors: next } });
  };

  const ruleSummary = (r) => {
    const conds = [];
    const c = r.conditions || {};
    if (c.project_types?.length) conds.push(`type ∈ ${c.project_types.join(",")}`);
    if (c.categories?.length) conds.push(`cat ∈ ${c.categories.join(",")}`);
    if (c.regions?.length) conds.push(`region ∈ ${c.regions.join(",")}`);
    if (c.min_value) conds.push(`≥ $${c.min_value.toLocaleString()}`);
    if (c.max_value) conds.push(`≤ $${c.max_value.toLocaleString()}`);
    const acts = [];
    const a = r.actions || {};
    if (a.fee_percent != null) acts.push(`fee ${a.fee_percent}%`);
    if (a.margin_percent != null) acts.push(`margin ${a.margin_percent}%`);
    if (a.contingency_percent != null) acts.push(`cont ${a.contingency_percent}%`);
    const lossN = Object.keys(a.loss_factors || {}).length;
    if (lossN > 0) acts.push(`loss × ${lossN}`);
    return { conds: conds.length ? conds.join(" · ") : "Any project", acts: acts.length ? acts.join(" · ") : "—" };
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto" data-testid="workspace-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-bold flex items-center gap-2">
            <Settings className="w-7 h-7 text-primary" /> Company Workspace
          </h1>
          <p className="text-muted-foreground text-sm">Global rules engine — applied automatically to matching cases.</p>
        </div>
        {editing == null && (
          <Button onClick={startNew} className="gap-2" data-testid="new-rule-btn"><Plus className="w-4 h-4" /> New Rule</Button>
        )}
      </div>

      {/* Editor */}
      {editing != null && (
        <div className="swiss-card space-y-4" data-testid="rule-editor">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-heading font-semibold">{editing === "new" ? "New Rule" : "Edit Rule"}</h2>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={cancel}><X className="w-4 h-4 mr-1" />Cancel</Button>
              <Button onClick={save} data-testid="save-rule-btn"><Save className="w-4 h-4 mr-1" />Save</Button>
            </div>
          </div>

          {/* Basics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2 space-y-1">
              <Label>Name *</Label>
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="rule-name" />
            </div>
            <div className="space-y-1">
              <Label>Priority</Label>
              <Input type="number" value={draft.priority} onChange={(e) => setDraft({ ...draft, priority: parseInt(e.target.value) || 100 })} />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Description</Label>
            <Textarea rows={2} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={draft.active} onCheckedChange={(c) => setDraft({ ...draft, active: !!c })} />
            Active
          </label>

          {/* Conditions */}
          <div className="border rounded p-3 space-y-3 bg-muted/30">
            <h3 className="font-semibold text-sm">When (conditions) — empty = match any</h3>

            <div>
              <Label className="text-xs">Project type</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {PROJECT_TYPES.map(t => (
                  <button key={t} type="button"
                    onClick={() => toggleArrayItem("project_types", t)}
                    className={`px-2 py-1 text-xs rounded border ${draft.conditions.project_types.includes(t) ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                    data-testid={`cond-pt-${t}`}>{t}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Category</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {CATEGORIES.map(t => (
                  <button key={t} type="button"
                    onClick={() => toggleArrayItem("categories", t)}
                    className={`px-2 py-1 text-xs rounded border ${draft.conditions.categories.includes(t) ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                    data-testid={`cond-cat-${t}`}>{t}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Region</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {REGIONS.map(t => (
                  <button key={t} type="button"
                    onClick={() => toggleArrayItem("regions", t)}
                    className={`px-2 py-1 text-xs rounded border ${draft.conditions.regions.includes(t) ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                    data-testid={`cond-reg-${t}`}>{t}</button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Min value ($)</Label>
                <Input type="number" value={draft.conditions.min_value ?? ""}
                  onChange={(e) => setDraft({ ...draft, conditions: { ...draft.conditions, min_value: e.target.value === "" ? null : parseFloat(e.target.value) } })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Max value ($)</Label>
                <Input type="number" value={draft.conditions.max_value ?? ""}
                  onChange={(e) => setDraft({ ...draft, conditions: { ...draft.conditions, max_value: e.target.value === "" ? null : parseFloat(e.target.value) } })} />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="border rounded p-3 space-y-3 bg-muted/30">
            <h3 className="font-semibold text-sm">Then (actions) — empty = inherit from previous rules / manual</h3>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Fee %</Label>
                <Input type="number" value={draft.actions.fee_percent ?? ""}
                  onChange={(e) => setDraft({ ...draft, actions: { ...draft.actions, fee_percent: e.target.value === "" ? null : parseFloat(e.target.value) } })}
                  data-testid="action-fee" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Margin %</Label>
                <Input type="number" value={draft.actions.margin_percent ?? ""}
                  onChange={(e) => setDraft({ ...draft, actions: { ...draft.actions, margin_percent: e.target.value === "" ? null : parseFloat(e.target.value) } })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Contingency %</Label>
                <Input type="number" value={draft.actions.contingency_percent ?? ""}
                  onChange={(e) => setDraft({ ...draft, actions: { ...draft.actions, contingency_percent: e.target.value === "" ? null : parseFloat(e.target.value) } })} />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Loss factors by trade (%) — leave blank to skip</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {TRADES.map(t => (
                  <div key={t} className="flex items-center gap-1">
                    <span className="text-xs capitalize w-24 truncate" title={t}>{t}</span>
                    <Input type="number" placeholder="—" value={draft.actions.loss_factors?.[t] ?? ""}
                      onChange={(e) => setLossFactor(t, e.target.value)}
                      className="h-7 text-xs"
                      data-testid={`loss-${t}`} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rules list */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : rules.length === 0 ? (
        <div className="swiss-card text-center py-12">
          <Settings className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
          <p className="text-muted-foreground">No rules yet. Create one to start automating defaults.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rules.map(r => {
            const s = ruleSummary(r);
            return (
              <div key={r.id} className="swiss-card flex items-start gap-3" data-testid={`rule-${r.id}`}>
                <div className="flex flex-col items-center pt-1 min-w-12">
                  <Badge variant="outline" className="font-mono">P{r.priority}</Badge>
                  {r.active ? <CheckCircle2 className="w-4 h-4 text-green-600 mt-1" /> : <AlertCircle className="w-4 h-4 text-amber-600 mt-1" />}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold">{r.name}</h3>
                  {r.description && <p className="text-xs text-muted-foreground">{r.description}</p>}
                  <p className="text-xs mt-1"><span className="text-muted-foreground">When:</span> {s.conds}</p>
                  <p className="text-xs"><span className="text-muted-foreground">Then:</span> {s.acts}</p>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => startEdit(r)} data-testid={`edit-${r.id}`}><Edit3 className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(r.id)} className="text-destructive" data-testid={`delete-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
